import "./RightSideBarTaskView.scss";
import { useEditor, EditorContent } from "@tiptap/react";
import {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useTranslation } from "react-i18next";
import StarterKit from "@tiptap/starter-kit";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { EmoticonReplacer } from "../../utils/emoticonExtension";
import {
  Bold01,
  Dotpoints01,
  Dotpoints02,
  Italic01,
  Hash01,
  Code01,
  MessageSquare01,
  Strikethrough01,
} from "@untitled-ui/icons-react";
import Links from "../Links/Links";
import TaskImages from "../TaskImages/TaskImages";
import ToolTip from "../ToolTip/ToolTip";
import Modal from "../Modal/Modal";
import TextColorPicker from "../../pages/Notes/Modals/TextColorPicker/TextColorPicker";
import EmojiPickerModal from "../../pages/Notes/Modals/EmojiPickerModal/EmojiPickerModal";
import { fetcher } from "../../utils/fetcher";
import { useResync } from "../../hooks/useRealtime";
import { useAutosave } from "../../hooks/useAutosave";
import useEditorActiveFormats from "../../hooks/useEditorActiveFormats";

const RightSideBarTaskView = forwardRef(function RightSideBarTaskView(
  {
    taskData,
    hideRightSideBar = () => {},
    setEditingDescription,
    currentTask,
    fetchTasks,
    activeTab = 0,
    focusAtEnd = false,
  },
  ref,
) {
  const { t, i18n } = useTranslation();
  // Use currentTask if available, otherwise fall back to taskData
  const [activeTask, setActiveTask] = useState(currentTask || taskData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const initialContentRef = useRef("");
  const lastSavedContentRef = useRef("");
  // Revision of the description shown in the editor - sent with saves so the
  // server can reject them if the task was changed on another device
  const descriptionRevRef = useRef(null);
  const loadedTaskIdRef = useRef(null);
  // Set when the server rejected our save, so the latest version replaces local edits
  const forceReloadRef = useRef(false);
  const [conflictReloadCount, setConflictReloadCount] = useState(0);

  const toolTipRef = useRef();
  const modalRef = useRef(null);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => hasUnsavedChanges,
    saveChanges: handleUpdate,
  }));

  const showToolTip = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const handleTextColorPicker = (e) => {
    openModal(
      e,
      "top-center",
      <TextColorPicker
        onSelectColor={(color) => {
          editor?.chain().focus().setColor(color).run();
          closeModal();
        }}
      />,
    );
  };

  const handleEmojiPicker = (e) => {
    openModal(
      e,
      "top-center",
      <EmojiPickerModal
        onSelectEmoji={(emoji) => {
          editor?.commands.insertContent(emoji);
          closeModal();
        }}
      />,
    );
  };

  // Update activeTask when currentTask or taskData changes
  useEffect(() => {
    setActiveTask(currentTask || taskData);
  }, [currentTask, taskData]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TaskList,
      TaskItem.configure({ nested: true }),
      TextStyle,
      Color,
      EmoticonReplacer,
    ],
    content: activeTask?.description || "",
    editable: true,
    onUpdate: ({ editor }) => {
      const currentContent = editor.getHTML();
      setHasUnsavedChanges(currentContent !== initialContentRef.current);
    },
  });
  const activeFormats = useEditorActiveFormats(editor);

  // Load the task's description into the editor when it changes - but never
  // overwrite text being typed (e.g. when our own autosave comes back from the
  // server), unless the server rejected that text as outdated
  useEffect(() => {
    if (!editor || activeTask?.description === undefined) return;

    const currentContent = editor.getHTML();
    const hasLocalEdits = currentContent !== lastSavedContentRef.current;
    const isSameTask = loadedTaskIdRef.current === activeTask._id;
    if (isSameTask && hasLocalEdits && !forceReloadRef.current) {
      return;
    }

    const newContent = activeTask.description || "";
    if (currentContent !== newContent) {
      editor.commands.setContent(newContent);
    }
    // Compare against the editor's own HTML so formatting differences don't count as edits
    initialContentRef.current = editor.getHTML();
    lastSavedContentRef.current = editor.getHTML();
    descriptionRevRef.current = activeTask.rev;
    loadedTaskIdRef.current = activeTask._id;
    forceReloadRef.current = false;
    setHasUnsavedChanges(false);
  }, [
    editor,
    activeTask?._id,
    activeTask?.description,
    activeTask?.rev,
    conflictReloadCount,
  ]);

  // If our save was rejected because the task changed on another device,
  // replace the local edits with the latest version
  useResync((reason, conflictId) => {
    if (reason === "conflict" && conflictId === activeTask?._id) {
      forceReloadRef.current = true;
      setConflictReloadCount((count) => count + 1);
    }
  });

  // Focus at the end of the editor content when focusAtEnd is true
  useEffect(() => {
    if (editor && focusAtEnd) {
      // Set focus and move cursor to the end
      editor.commands.focus();
      editor.commands.setTextSelection(editor.state.doc.content.size);
    }
  }, [editor, focusAtEnd]);

  // Auto-save in the background once the user has paused typing
  const { scheduleSave, isAutosaving } = useAutosave({
    enabled: autoSaveEnabled && activeTab === 0,
    hasChanges: () =>
      !!editor && editor.getHTML() !== lastSavedContentRef.current,
    save: () => handleUpdate(false, true),
  });

  useEffect(() => {
    if (!editor) return;
    editor.on("update", scheduleSave);
    return () => editor.off("update", scheduleSave);
  }, [editor, scheduleSave]);

  const handleUpdate = async (closeSidebar = true, isBackground = false) => {
    if (editor && activeTask) {
      const newDescription = editor.getHTML();

      // Don't save if nothing changed
      if (newDescription === lastSavedContentRef.current) {
        if (closeSidebar) {
          hideRightSideBar();
        }
        return;
      }

      if (isBackground) {
        // Background save: NO local state updates to avoid interrupting typing
        // The server has the data - that's all that matters. UI will sync when sidebar closes.
        return fetcher
          .put(`/api/tasks/${activeTask._id}`, {
            rev: descriptionRevRef.current,
            description: newDescription,
          })
          .then((savedTask) => {
            // Update ONLY the refs and timestamp - don't touch activeTask or editingDescription
            lastSavedContentRef.current = newDescription;
            descriptionRevRef.current = savedTask.rev;
            setLastSaved(new Date());
          })
          .catch((err) => {
            console.error("Error updating task (background):", err);
          });
      }

      // Manual save: show feedback and wait for response
      setIsSaving(true);

      try {
        // Update the task in the database
        // Only send the description field to avoid overwriting other fields with stale data
        const savedTask = await fetcher.put(`/api/tasks/${activeTask._id}`, {
          rev: descriptionRevRef.current,
          description: newDescription,
        });
        descriptionRevRef.current = savedTask.rev;

        // Call setEditingDescription if available (for local state update)
        if (setEditingDescription) {
          setEditingDescription(newDescription);
        }

        // Update activeTask with new description
        setActiveTask((prev) => ({ ...prev, description: newDescription }));

        // Reset unsaved changes state
        initialContentRef.current = newDescription;
        lastSavedContentRef.current = newDescription;
        setHasUnsavedChanges(false);
        setLastSaved(new Date());

        // Close the right sidebar after updating only if closeSidebar is true
        if (closeSidebar) {
          hideRightSideBar();
        }
      } catch (err) {
        console.error("Error updating task:", err);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleTaskUpdate = (updatedTask) => {
    setActiveTask(updatedTask);

    // Don't call fetchTasks() here - it interrupts typing in description
    // The task list will be refreshed when sidebar closes (hideRightSideBar calls fetchTasks)
  };

  return (
    <div className="right-side-bar-task-view">
      <ToolTip ref={toolTipRef}></ToolTip>

      <div className="body-wrapper">
        {activeTab === 0 && (
          <>
            <h3 className="section-title">{t("common.description")}</h3>
            <div className="text-editor-wrapper">
              <div className="text-editor-buttons">
                {/* Text Formatting */}
                <div
                  className={`text-editor-button ${activeFormats.bold ? "is-active" : ""}`}
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("lists.editor.bold"))
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_bold
                  </span>
                </div>
                <div
                  className={`text-editor-button ${activeFormats.italic ? "is-active" : ""}`}
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("lists.editor.italic"))
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_italic
                  </span>
                </div>
                <div
                  className={`text-editor-button ${activeFormats.strike ? "is-active" : ""}`}
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  onMouseEnter={(e) =>
                    showToolTip(
                      e,
                      "top-center",
                      t("lists.editor.strikethrough"),
                    )
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_strikethrough
                  </span>
                </div>

                {/* Separator */}
                <div className="text-editor-separator"></div>

                {/* Headings */}
                <div
                  className={`text-editor-button ${activeFormats.h1 ? "is-active" : ""}`}
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(
                      e,
                      "top-center",
                      t("taskSidebar.heading", { level: 1 }),
                    )
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_h1
                  </span>
                </div>
                <div
                  className={`text-editor-button ${activeFormats.h2 ? "is-active" : ""}`}
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(
                      e,
                      "top-center",
                      t("taskSidebar.heading", { level: 2 }),
                    )
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_h2
                  </span>
                </div>
                <div
                  className={`text-editor-button ${activeFormats.h3 ? "is-active" : ""}`}
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 3 }).run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(
                      e,
                      "top-center",
                      t("taskSidebar.heading", { level: 3 }),
                    )
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_h3
                  </span>
                </div>

                {/* Separator */}
                <div className="text-editor-separator"></div>

                {/* Text Color */}
                <div
                  className="text-editor-button"
                  onClick={handleTextColorPicker}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("lists.editor.textColor"))
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_color_text
                  </span>
                </div>

                {/* Emoji */}
                <div
                  className="text-editor-button"
                  onClick={handleEmojiPicker}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("lists.editor.emoji"))
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    mood
                  </span>
                </div>

                {/* Separator */}
                <div className="text-editor-separator"></div>

                {/* Lists */}
                <div
                  className={`text-editor-button ${activeFormats.bulletList ? "is-active" : ""}`}
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("lists.editor.bulletList"))
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_list_bulleted
                  </span>
                </div>
                <div
                  className={`text-editor-button ${activeFormats.orderedList ? "is-active" : ""}`}
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("lists.editor.numberedList"))
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_list_numbered
                  </span>
                </div>
                <div
                  className={`text-editor-button ${activeFormats.taskList ? "is-active" : ""}`}
                  onClick={() => editor.chain().focus().toggleTaskList().run()}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("lists.editor.checklist"))
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    checklist
                  </span>
                </div>

                {/* Separator */}
                <div className="text-editor-separator"></div>

                {/* Block Elements */}
                <div
                  className={`text-editor-button ${activeFormats.blockquote ? "is-active" : ""}`}
                  onClick={() =>
                    editor.chain().focus().toggleBlockquote().run()
                  }
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("lists.editor.quote"))
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    format_quote
                  </span>
                </div>
                <div
                  className={`text-editor-button ${activeFormats.codeBlock ? "is-active" : ""}`}
                  onClick={() => {
                    if (!editor) return;

                    const { from, to } = editor.state.selection;
                    const selectedText = editor.state.doc.textBetween(
                      from,
                      to,
                      "\n",
                    );

                    // If we have selected text across multiple paragraphs
                    if (selectedText && selectedText.includes("\n")) {
                      editor
                        .chain()
                        .focus()
                        .deleteSelection()
                        .insertContent({
                          type: "codeBlock",
                          content: [{ type: "text", text: selectedText }],
                        })
                        .run();
                    } else {
                      // Normal toggle for single line or empty
                      editor.chain().focus().toggleCodeBlock().run();
                    }
                  }}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("lists.editor.codeBlock"))
                  }
                  onMouseLeave={() => toolTipRef.current?.close()}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px" }}
                  >
                    code
                  </span>
                </div>
              </div>
              <EditorContent editor={editor} className="tiptap-editor" />
              <div className="text-editor-bottom-bar">
                <div className="autosave-info">
                  <label className="autosave-toggle">
                    <input
                      type="checkbox"
                      checked={autoSaveEnabled}
                      onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                    />
                    <span>{t("tasks.links.autoSave")}</span>
                  </label>
                  {autoSaveEnabled && (
                    <span
                      className={`material-symbols-outlined autosave-icon ${
                        isSaving || isAutosaving ? "spinning" : ""
                      }`}
                    >
                      cached
                    </span>
                  )}
                  {(isSaving || isAutosaving) && (
                    <span className="save-status saving">
                      {t("common.saving")}
                    </span>
                  )}
                  {!isSaving && !isAutosaving && lastSaved && (
                    <span className="save-status saved">
                      {t("taskSidebar.savedAt", {
                        time: new Date(lastSaved).toLocaleTimeString(
                          i18n.language,
                        ),
                      })}
                    </span>
                  )}
                </div>
                <div
                  className="text-editor-update-button"
                  onClick={() => handleUpdate(true)}
                >
                  {t("taskSidebar.updateAndClose")}
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 1 && (
          <Links taskData={activeTask} onTaskUpdate={handleTaskUpdate} />
        )}

        {activeTab === 2 && (
          <TaskImages taskData={activeTask} onTaskUpdate={handleTaskUpdate} />
        )}
      </div>

      {/* <ul>
        <li>Status</li>
        <li>Person</li>
        <li>Priority</li>
        <li>Description</li>
      </ul>

      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {JSON.stringify(taskData, null, 2)}
      </pre> */}

      <Modal ref={modalRef} />
    </div>
  );
});

export default RightSideBarTaskView;
